<template>
  <div v-if="visible" class="overlay-modal">
    <div class="modal-adjuntos">
      <h4 class="modal-titulo">Adjuntar informes</h4>
      <p class="modal-sub">
        Ejecución <strong>#{{ idEjecucion }}</strong>
        <span v-if="periodo"> · periodo {{ periodo }}</span>
      </p>
      <p class="nota">
        Documentos que se generaron en su momento para esta autoevaluación.
        Una vez adjuntados no se pueden eliminar.
      </p>

      <!-- Archivos ya adjuntados -->
      <div class="seccion">
        <h5 class="seccion-titulo">
          Archivos adjuntos
          <span v-if="adjuntos.length" class="contador">{{ adjuntos.length }}</span>
        </h5>

        <p v-if="cargando" class="vacio">Cargando…</p>
        <p v-else-if="!adjuntos.length" class="vacio">Todavía no hay archivos adjuntos.</p>

        <ul v-else class="lista-adjuntos">
          <li v-for="a in adjuntos" :key="a.id_adjunto" class="item-adjunto">
            <div class="item-info">
              <span class="item-nombre" :title="a.nombre">{{ a.nombre }}</span>
              <span class="item-meta">
                {{ formatearTamano(a.bytes) }}
                <template v-if="a.usuario?.name"> · {{ a.usuario.name }}</template>
                <template v-if="a.created_at"> · {{ formatearFecha(a.created_at) }}</template>
              </span>
            </div>
            <div class="item-acciones">
              <a :href="a.url" target="_blank" rel="noopener noreferrer" class="btn-ver">Ver</a>
              <a
                :href="a.url_descarga || a.url"
                target="_blank"
                rel="noopener noreferrer"
                class="btn-descargar"
              >Descargar</a>
            </div>
          </li>
        </ul>
      </div>

      <!-- Selección de nuevos archivos -->
      <div class="seccion">
        <h5 class="seccion-titulo">Agregar archivos</h5>

        <label class="dropzone" :class="{ deshabilitada: subiendo }">
          <input
            ref="inputArchivos"
            type="file"
            multiple
            :accept="ACEPTADOS"
            :disabled="subiendo"
            class="input-oculto"
            @change="alSeleccionar"
          />
          <span v-if="!seleccionados.length">
            Seleccionar archivos (PDF, Word, Excel, PowerPoint, imágenes)
          </span>
          <span v-else>{{ seleccionados.length }} archivo(s) seleccionado(s)</span>
        </label>

        <ul v-if="seleccionados.length" class="lista-seleccion">
          <li v-for="(f, i) in seleccionados" :key="i" class="item-seleccion">
            <span class="item-nombre" :title="f.name">{{ f.name }}</span>
            <span class="item-meta">{{ formatearTamano(f.size) }}</span>
            <button
              type="button"
              class="btn-quitar"
              :disabled="subiendo"
              title="Quitar de la selección"
              @click="quitar(i)"
            >✕</button>
          </li>
        </ul>

      </div>

      <div class="botones">
        <button class="btn salir" :disabled="subiendo" @click="cerrar">Cerrar</button>
        <button
          class="btn guardar"
          :disabled="subiendo || !seleccionados.length"
          @click="subir"
        >
          {{ subiendo ? 'Subiendo…' : `Subir ${seleccionados.length || ''}`.trim() }}
        </button>
      </div>

      <MensajeFlotante
        :visible="mensajeVisible"
        :mensaje="mensajeTexto"
        :tipo="mensajeTipo"
        @cerrar="mensajeVisible = false"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Adjuntar informes externos a una ejecución antigua.
 *
 * Solo se agregan y se consultan: por diseño no hay edición ni borrado.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, watch } from 'vue'
import type InformeAdjunto from '@/interfaces/InformeAdjunto'
import {
  listarInformesAdjuntosService,
  subirInformesAdjuntosService
} from '@/services/InformesAdjuntosService'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

const props = defineProps<{
  visible: boolean
  idEjecucion: number | null
  periodo?: string | null
}>()

const emit = defineEmits<{
  (e: 'cerrar'): void
  (e: 'subido', cantidad: number): void
}>()

// Debe coincidir con las extensiones que valida el backend.
const ACEPTADOS = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.png,.jpg,.jpeg,.txt'
const MAX_BYTES = 20 * 1024 * 1024

const adjuntos = ref<InformeAdjunto[]>([])
const seleccionados = ref<File[]>([])
const cargando = ref(false)
const subiendo = ref(false)
const inputArchivos = ref<HTMLInputElement | null>(null)
const mensajeVisible = ref(false)
const mensajeTexto   = ref('')
const mensajeTipo    = ref<'positivo' | 'error'>('positivo')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value   = texto
  mensajeTipo.value    = tipo
  mensajeVisible.value = true
}

watch(
  () => [props.visible, props.idEjecucion] as const,
  ([visible]) => {
    if (visible) {
      seleccionados.value = []
      mensajeVisible.value = false
      cargar()
    }
  },
  { immediate: true }
)

async function cargar() {
  if (!props.idEjecucion) return
  cargando.value = true
  try {
    adjuntos.value = await listarInformesAdjuntosService(props.idEjecucion)
  } finally {
    cargando.value = false
  }
}

function alSeleccionar(evento: Event) {
  const input = evento.target as HTMLInputElement
  const elegidos = Array.from(input.files ?? [])

  const pesados = elegidos.filter(f => f.size > MAX_BYTES)
  if (pesados.length) {
    mostrarMensaje(
      `Estos archivos superan el límite de 20 MB: ${pesados.map(f => f.name).join(', ')}`,
      'error'
    )
  }

  const validos = elegidos.filter(f => f.size <= MAX_BYTES)
  for (const f of validos) {
    const yaEsta = seleccionados.value.some(s => s.name === f.name && s.size === f.size)
    if (!yaEsta) seleccionados.value.push(f)
  }

  if (inputArchivos.value) inputArchivos.value.value = ''
}

function quitar(indice: number) {
  seleccionados.value.splice(indice, 1)
}

async function subir() {
  if (!props.idEjecucion || !seleccionados.value.length) return
  subiendo.value = true
  try {
    const res = await subirInformesAdjuntosService(props.idEjecucion, seleccionados.value)
    if (!res.ok) {
      mostrarMensaje(res.error ?? 'No se pudieron subir los archivos.', 'error')
      return
    }
    const cantidad = res.adjuntos?.length ?? seleccionados.value.length
    seleccionados.value = []
    await cargar()
    emit('subido', cantidad)
  } finally {
    subiendo.value = false
  }
}

function cerrar() {
  if (subiendo.value) return
  emit('cerrar')
}

function formatearTamano(bytes?: number | null): string {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatearFecha(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' })
}
</script>

<style scoped>
.overlay-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-adjuntos {
  background: #fff;
  border-radius: 10px;
  padding: 1.5rem;
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  font-family: var(--font-family, inherit);
}

.modal-titulo {
  margin: 0 0 0.25rem;
  font-size: 1.15rem;
  font-weight: 700;
}

.modal-sub {
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  color: #666;
}

.nota {
  margin: 0 0 1.25rem;
  padding: 0.6rem 0.75rem;
  border-left: 3px solid #eea222;
  background: #fdf6e7;
  border-radius: 4px;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #5c4a1e;
}

.seccion {
  margin-bottom: 1.25rem;
}

.seccion-titulo {
  margin: 0 0 0.6rem;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contador {
  background: #0e69a0;
  color: #fff;
  border-radius: 10px;
  padding: 0 0.5rem;
  font-size: 0.72rem;
  line-height: 1.5;
}

.vacio {
  margin: 0;
  font-size: 0.85rem;
  color: #888;
}

.lista-adjuntos,
.lista-seleccion {
  list-style: none;
  margin: 0;
  padding: 0;
}

.item-adjunto,
.item-seleccion {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid #ececec;
  border-radius: 6px;
  margin-bottom: 0.4rem;
  background: #fafafa;
}

.item-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.item-nombre {
  font-size: 0.85rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.item-meta {
  font-size: 0.72rem;
  color: #888;
  white-space: nowrap;
}

.item-acciones {
  display: flex;
  gap: 0.4rem;
  flex-shrink: 0;
}

.btn-ver,
.btn-descargar {
  padding: 0.3rem 0.85rem;
  border-radius: 5px;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

.btn-ver {
  background: #0e69a0;
  color: #fff;
}

.btn-ver:hover {
  background: #0a5480;
}

.btn-descargar {
  background: #fff;
  color: #0e69a0;
  border: 1px solid #0e69a0;
}

.btn-descargar:hover {
  background: #eef4fa;
}

.btn-quitar {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #b00020;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 0.25rem;
}

.btn-quitar:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dropzone {
  display: block;
  padding: 1.1rem;
  border: 2px dashed #cfcfcf;
  border-radius: 8px;
  text-align: center;
  font-size: 0.83rem;
  color: #666;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}

.dropzone:hover {
  border-color: #0e69a0;
  background: #f4f9fd;
}

.dropzone.deshabilitada {
  opacity: 0.55;
  cursor: not-allowed;
}

.input-oculto {
  display: none;
}

.lista-seleccion {
  margin-top: 0.6rem;
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.btn {
  padding: 8px 20px;
  height: 38px;
  font-weight: 600;
  font-size: 0.83rem;
  font-family: inherit;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.salir {
  background: #fff;
  border: 1px solid #ccc;
}

.btn.guardar {
  background: #eea222;
  color: #000;
}

.btn.guardar:not(:disabled):hover {
  background: #0e69a0;
  color: #fff;
}
</style>
